/*
Origin Core 1.4.0

called from /layout/theme.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
<localization-form-component> — handles country search, filtering, and selection
within Origin's dialog-component modal. Extends Component for full ref/on: system support.

Adapted from Horizon's LocalizationFormComponent. Key Origin differences:
- Two-step UX: selecting a country updates the form; the Continue button submits it
- Language selector auto-submits when changed
- Dialog open/close is handled by dialog-component (not dropdown-localization-component)
- Geolocation/market detection runs at module load (Origin-specific, no Horizon equivalent)
*/

import { Component } from '@theme/component';
import { normalizeString } from '@theme/utilities';

if (!customElements.get('localization-form-component')) {
  customElements.define(
    'localization-form-component',
    class LocalizationFormComponent extends Component {

      /** @type {MutationObserver | null} */
      #dialogObserver = null;

      connectedCallback() {
        super.connectedCallback();

        this.refs.search?.addEventListener('keydown', this.#onSearchKeyDown);
        this.refs.countryList?.addEventListener('keydown', this.#onContainerKeyDown);
        this.refs.countryList?.addEventListener('scroll', this.#onCountryListScroll);

        // Hook into the parent dialog: reset form on close, auto-focus search on open
        const dialog = this.closest('dialog');
        if (dialog) {
          dialog.addEventListener('close', () => this.resetForm());
          this.#dialogObserver = new MutationObserver((mutations) => {
            for (const m of mutations) {
              if (m.attributeName === 'open' && dialog.hasAttribute('open')) {
                setTimeout(() => this.focusSearchInput(), 50);
              }
            }
          });
          this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
        }

        // Resizing is expensive for browsers without field-sizing: content support
        if (this.refs.languageInput) {
          setTimeout(() => this.resizeLanguageInput(), 0);
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.refs.search?.removeEventListener('keydown', this.#onSearchKeyDown);
        this.refs.countryList?.removeEventListener('keydown', this.#onContainerKeyDown);
        this.refs.countryList?.removeEventListener('scroll', this.#onCountryListScroll);
        this.#dialogObserver?.disconnect();
        this.#dialogObserver = null;
      }

      /**
       * Filters the country list based on the search input value.
       * Matches against country name, aliases (e.g. "usa"), ISO code, and currency.
       * Called via on:keyup="/filterCountries" on the search input.
       */
      filterCountries() {
        const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
          this.refs;

        const searchValue = normalizeString(search.value);
        let countVisible = 0;

        resetButton.toggleAttribute('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.toggleAttribute('hidden', Boolean(searchValue));
        }

        for (const item of countryListItems) {
          if (!searchValue) {
            item.removeAttribute('hidden');
            const span = item.querySelector('.country');
            // eslint-disable-next-line no-self-assign
            if (span) span.textContent = span.textContent; // clear any highlight markup
            countVisible++;
          } else {
            const matches = this.#findMatches(searchValue, item);

            if (matches.label || matches.alias || matches.iso || matches.currency) {
              item.removeAttribute('hidden');
              const span = item.querySelector('.country');
              if (span) span.innerHTML = this.#highlightMatch(span.textContent, search.value);
              countVisible++;
            } else {
              item.setAttribute('hidden', '');
            }
          }
        }

        if (liveRegion) {
          liveRegion.innerText = searchValue
            ? (this.dataset.labelResultsCount ?? '').replace('[count]', `${countVisible}`)
            : '';
        }

        if (noResultsMessage) noResultsMessage.hidden = countVisible > 0;
        if (countryList) countryList.scrollTop = 0;
      }

      /**
       * Updates the hidden country_code input and visual selection state.
       * Origin uses a two-step UX: this updates the selection, Continue button submits.
       * Called via on:click="/selectCountry/" on each list item.
       *
       * @param {string} isoCode - The country ISO code to select.
       * @param {Event} event
       */
      selectCountry(isoCode, event) {
        event.preventDefault();
        const { countryInput, countryListItems, shipTo } = this.refs;

        if (countryInput) countryInput.value = isoCode;

        for (const item of countryListItems) {
          if (item.dataset.value === isoCode) {
            item.setAttribute('aria-current', 'true');
            if (shipTo) shipTo.textContent = item.querySelector('.country')?.textContent.trim() ?? '';
          } else {
            item.removeAttribute('aria-current');
          }
        }
      }

      /**
       * Auto-submits the form when the language selector changes.
       * Called via on:change="/changeLanguage" on the language select.
       *
       * @param {Event} event
       */
      changeLanguage(event) {
        const { form, languageInput } = this.refs;
        const value = event.target instanceof HTMLSelectElement ? event.target.value : null;
        if (!value) return;
        languageInput.value = value;
        this.resizeLanguageInput();
        form.submit();
      }

      /**
       * Clears the search filter and resets state.
       * Called via on:click="/resetCountriesFilter" on the reset button.
       *
       * @param {Event} event
       */
      resetCountriesFilter(event) {
        event.stopPropagation();
        this.refs.search.value = '';
        this.filterCountries();
        this.refs.search.setAttribute('aria-activedescendant', '');
        this.refs.search.focus();
      }

      /**
       * Resets the form to its initial state. Called when the dialog closes.
       */
      resetForm() {
        const { search } = this.refs;
        if (!search || search.value === '') return;
        search.value = '';
        this.filterCountries();
        search.setAttribute('aria-activedescendant', '');
      }

      /**
       * Focuses the search input. Called automatically when the dialog opens.
       */
      focusSearchInput = () => {
        this.refs.search?.focus();
      };

      /**
       * Resizes the language <select> to fit its selected option text.
       * No-op if the browser supports field-sizing: content natively.
       */
      resizeLanguageInput() {
        const { languageInput } = this.refs;
        if (!languageInput || CSS.supports('field-sizing', 'content')) return;

        // Temporarily hide non-selected options to measure intrinsic width
        for (const option of languageInput.options) {
          if (!option.selected) {
            option.dataset.optionLabel = option.textContent ?? '';
            option.innerText = '';
          }
        }

        languageInput.style.width = 'fit-content';
        const width = Math.ceil(languageInput.offsetWidth) + 1;
        if (width > 0) languageInput.style.width = `${width}px`;

        for (const option of languageInput.options) {
          if (option.dataset.optionLabel) {
            option.textContent = option.dataset.optionLabel;
            delete option.dataset.optionLabel;
          }
        }
      }

      /**
       * Handles keydown on the search input.
       * Prevents Enter from submitting; delegates arrow keys to list navigation.
       *
       * @param {KeyboardEvent} event
       */
      #onSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this.#onContainerKeyDown(event);
      };

      /**
       * Handles arrow key navigation and Enter selection within the country list.
       *
       * @param {KeyboardEvent} event
       */
      #onContainerKeyDown = (event) => {
        const { countryInput, countryListItems } = this.refs;

        switch (event.key) {
          case 'ArrowUp':
            event.preventDefault();
            event.stopPropagation();
            this.#changeCountryFocus('UP');
            break;

          case 'ArrowDown':
            event.preventDefault();
            event.stopPropagation();
            this.#changeCountryFocus('DOWN');
            break;

          case 'Enter': {
            event.preventDefault();
            event.stopPropagation();
            const focused = (countryListItems ?? []).find(
              (item) => item.getAttribute('aria-selected') === 'true'
            );
            if (focused && countryInput) {
              this.selectCountry(focused.dataset.value ?? '', event);
            }
            break;
          }
        }

        if (!this.refs.search) return;

        setTimeout(() => {
          const focusable = (countryListItems ?? []).filter((item) => !item.hasAttribute('hidden'));
          const idx = focusable.findIndex((item) => item === document.activeElement);
          this.refs.search.setAttribute('aria-activedescendant', idx > -1 ? focusable[idx].id : '');
        });
      };

      /**
       * Moves focus to the previous or next visible list item.
       * Manages aria-selected state for screen reader announcement.
       *
       * @param {'UP' | 'DOWN'} direction
       */
      #changeCountryFocus(direction) {
        const { countryListItems } = this.refs;
        const focusable = (countryListItems ?? []).filter((item) => !item.hasAttribute('hidden'));
        const currentIdx = focusable.findIndex((item) => item === document.activeElement);
        const current = focusable[currentIdx];

        const nextIdx =
          direction === 'UP'
            ? currentIdx > 0 ? currentIdx - 1 : focusable.length - 1
            : currentIdx < focusable.length - 1 ? currentIdx + 1 : 0;

        const next = focusable[nextIdx];

        if (current) current.setAttribute('aria-selected', 'false');
        next?.setAttribute('aria-selected', 'true');
        next?.focus();
      }

      /**
       * Adds a visual border below the search area when the country list is scrolled.
       *
       * @param {Event} event
       */
      #onCountryListScroll = (event) => {
        const filter = this.querySelector('.country-filter');
        const list = event.target instanceof HTMLElement ? event.target : null;
        if (filter && list) filter.classList.toggle('is-scrolled', list.scrollTop > 0);
      };

      /**
       * Tests whether a country list item matches the search value across
       * name, aliases, ISO code, and currency.
       *
       * @param {string} searchValue - Normalised search string.
       * @param {HTMLElement} item - The country <li> element.
       * @returns 
       */
      #findMatches(searchValue, item) {
        const { aliases, value: iso } = item.dataset;

        const countryName = normalizeString(item.querySelector('.country')?.textContent ?? '');
        const currency = normalizeString(
          item.querySelector('.localization-form__currency')?.textContent ?? ''
        );
        const countryAliases = aliases?.split(',').map((a) => normalizeString(a)) ?? [];

        return {
          label: countryName.includes(searchValue),
          iso: normalizeString(iso ?? '') === searchValue,
          currency: currency.includes(searchValue),
          alias: countryAliases.some((a) => a.startsWith(searchValue)),
        };
      }

      /**
       * Wraps the matching portion of text in <mark> tags for visual highlighting.
       * NOTE: correctly wraps the match itself (not before/after as in Horizon's implementation).
       *
       * @param {string | null} text - Original country name text.
       * @param {string} rawQuery - The raw (un-normalised) search input value.
       * @returns {string}
       */
      #highlightMatch(text, rawQuery) {
        if (!text || !rawQuery) return text ?? '';
        const query = normalizeString(rawQuery);
        const normalizedText = normalizeString(text);
        const start = normalizedText.indexOf(query);
        if (start === -1) return text;
        const end = start + query.length;
        return text.slice(0, start) + '<mark>' + text.slice(start, end) + '</mark>' + text.slice(end);
      }
    }
  );
}

// =============================================================
// Market / Geolocation detection
// Origin-specific — no Horizon equivalent.
//
// Detects the user's country via Shopify's browsing_context_suggestions
// endpoint and shows a popup if they appear to be browsing from a
// different market than their current URL.
//
// Markets come from the "Geolocation" theme settings, NOT from a hardcoded array. Origin used to
// ship Unified's own dev-store URLs here, which meant a freshly-forked project would offer to
// redirect real customers to unified-origin.myshopify.com. The feature is off until an editor
// enables it and supplies the markets for that store.
// =============================================================

/** @type {Array<{name: string, countries: string[], storeName: string|null, url: string|null}>} */
let markets = [];

try {
  const configured = JSON.parse("[]");

  if (Array.isArray(configured)) {
    markets = configured
      .filter((m) => m && typeof m.name === 'string' && Array.isArray(m.countries))
      .map((m) => ({
        name: m.name,
        countries: m.countries,
        storeName: m.storeName ?? null,
        url: m.url ?? null,
      }));
  }
} catch (error) {
  console.error('Geolocation: "Markets" theme setting is not valid JSON.', error);
}

const fallbackMarket = markets[0] ?? null;

const hrefNormalised = window.location.href.split('?')[0].replace(/\/?$/, '/');

function findCurrentMarket() {
  return markets
    .filter((m) => m.url)
    .sort((a, b) => b.url.length - a.url.length)
    .find((m) => hrefNormalised.startsWith(m.url.replace(/\/?$/, '/'))) ?? fallbackMarket;
}

async function fetchRoutes() {
  const currentMarket = findCurrentMarket();

  const response = await fetch(
    window.Shopify.routes.root
      + 'browsing_context_suggestions.json'
      + '?country[enabled]=true'
      + `&country[exclude]=${currentMarket.countries[0]}`
      + '&language[enabled]=true'
      + `&language[exclude]=${window.Shopify.language}`
  );
  return response.json();
}

const geolocationEnabled = false;

// Only run when the feature is on, markets are configured, and the visitor hasn't dismissed it.
if (geolocationEnabled && markets.length && getCookie('show_geo_popup') !== 'false') {
  fetchRoutes().then((res) => {
    const detected = res?.detected_values ?? {};
    const country_handle = detected.country?.handle;
    // The endpoint nests the display name under country.name; country_name is kept as a fallback
    // in case the shape differs, and the ISO code is the last resort so the popup is never blank.
    const country_name = detected.country?.name ?? detected.country_name ?? country_handle;

    if (!country_handle) return;

    const localizationInMarket = markets.find((m) => m.countries.includes(country_handle));
    const currentMarket = findCurrentMarket();

    if (localizationInMarket && localizationInMarket !== currentMarket) {
      window.dispatchEvent(
        new CustomEvent('geolocator-show', {
          detail: { open: true, newMarket: localizationInMarket, country_code: country_handle, country_name },
        })
      );
    }
  });
}

window.addEventListener('cookie_geo_preference', () => setCookie('show_geo_popup', false, 0.5));

function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value ?? ''}${expires}; path=/`;
}

function getCookie(name) {
  const nameEQ = `${name}=`;
  for (let c of document.cookie.split(';')) {
    c = c.trim();
    if (c.startsWith(nameEQ)) return c.substring(nameEQ.length);
  }
  return null;
}
